import { unwrapResult } from "@reduxjs/toolkit"
import CheckIcon from "@mui/icons-material/Check"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    FormControl,
    InputLabel,
    LinearProgress,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react"

import { useAppDispatch } from "../../../app/hooks"
import {
    DOCUMENT_CONTENT_ACTIONS,
    DOCUMENT_STATUSES,
    DocumentContentActions,
    DocumentStatus,
} from "../../documents/documentsApi"
import { DocumentStatusLabel } from "../../documents/DocumentStatusLabel"
import {
    loadDocumentContentActionRules,
    SysDocumentContentActionRule,
    SubjectType,
    upsertSysDocumentContentActionRule,
} from "./systemWorkflowApi"
import {
    normalizeActionCode,
    normalizeSubjectType,
    subjectTypeLabel,
    SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES,
} from "./systemWorkflowHelpers"

type EditMode = "single" | "column" | "row"

type DraftRuleChange = {
    documentStatus: DocumentStatus
    actionCode: DocumentContentActions
    subjectType: SubjectType
    enabled: boolean
}

const EDIT_MODES: { value: EditMode; label: string }[] = [
    {
        value: "single",
        label: "Pojedyncza reguła",
    },
    {
        value: "column",
        label: "Według kolumny",
    },
    {
        value: "row",
        label: "Według wiersza",
    },
]

// Kolory stanu reguł. Zielone zaznaczenie korzysta z theme.palette.success.
const RULE_COLORS = {
    enabled: "success.main",
    disabled: "text.secondary",
    changed: "warning.main",
}

// Odstęp między panelem a dolną krawędzią widocznego obszaru przeglądarki.
const PANEL_BOTTOM_GAP = 50

// Zielone tło wiersza / kolumny oraz mocniejsze tło wybranej komórki.
const SELECTION_OPACITY = { axis: 0.08, cell: 0.18 }

const getCellKey = (
    actionCode: DocumentContentActions,
    subjectType: SubjectType
) => `${actionCode}__${subjectType}`

export const SysDocumentContentActionRulesSetup = () => {
    const dispatch = useAppDispatch()
    const componentId = useId()
    const statusLabelId = `${componentId}-status-label`
    const modeButtonId = `${componentId}-mode-button`
    const modeMenuId = `${componentId}-mode-menu`

    const panelRef = useRef<HTMLDivElement | null>(null)
    const [panelHeight, setPanelHeight] = useState<number>()
    const [rules, setRules] = useState<SysDocumentContentActionRule[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [hasLoaded, setHasLoaded] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [selectedStatus, setSelectedStatus] = useState<DocumentStatus>("N")
    const [editMode, setEditMode] = useState<EditMode>("single")
    const [editModeAnchor, setEditModeAnchor] = useState<HTMLElement | null>(null)
    const [actionSearch, setActionSearch] = useState("")
    const [selectedActionCode, setSelectedActionCode] =
        useState<DocumentContentActions | null>(null)
    const [selectedSubjectType, setSelectedSubjectType] =
        useState<SubjectType | null>(null)
    const [pendingCellKeys, setPendingCellKeys] = useState<Set<string>>(new Set())
    const [batchDraft, setBatchDraft] = useState<Record<string, DraftRuleChange>>({})

    const saveInProgress = useRef(false)
    const loadRequestId = useRef(0)

    const isSaving = pendingCellKeys.size > 0
    const isBusy = isLoading || isSaving
    const canEdit = hasLoaded && !isBusy
    const batchChangesCount = Object.keys(batchDraft).length
    const hasAnyBatchChanges = batchChangesCount > 0
    const isContextLocked = isBusy || hasAnyBatchChanges
    const editModeLabel = EDIT_MODES.find(mode => mode.value === editMode)!.label

    useLayoutEffect(() => {
        const panel = panelRef.current
        if (!panel) return

        let frameId = 0
        const viewport = window.visualViewport

        const measure = () => {
            const top = Math.max(0, panel.getBoundingClientRect().top)
            const viewportBottom = viewport
                ? viewport.offsetTop + viewport.height
                : window.innerHeight
            const availableHeight = Math.max(0, Math.floor(viewportBottom - top - PANEL_BOTTOM_GAP))

            setPanelHeight(current => current === availableHeight ? current : availableHeight)
        }

        const scheduleMeasure = () => {
            window.cancelAnimationFrame(frameId)
            frameId = window.requestAnimationFrame(measure)
        }

        // Wysokość obejmuje cały panel: filtry, tabelę oraz pasek przycisków.
        measure()
        const observer = new ResizeObserver(scheduleMeasure)
        for (let parent = panel.parentElement; parent; parent = parent.parentElement) {
            observer.observe(parent)
        }
        window.addEventListener("resize", scheduleMeasure)
        viewport?.addEventListener("resize", scheduleMeasure)

        return () => {
            window.cancelAnimationFrame(frameId)
            observer.disconnect()
            window.removeEventListener("resize", scheduleMeasure)
            viewport?.removeEventListener("resize", scheduleMeasure)
        }
    }, [])

    const loadRules = useCallback(async () => {
        const requestId = ++loadRequestId.current
        setIsLoading(true)
        setLoadError(null)

        try {
            const result = await dispatch(loadDocumentContentActionRules())
            const loadedRules = unwrapResult(result)

            if (requestId !== loadRequestId.current) return

            setRules(loadedRules)
            setHasLoaded(true)
        } catch {
            if (requestId !== loadRequestId.current) return

            setHasLoaded(false)
            setLoadError("Nie udało się pobrać reguł. Kliknij „Odśwież”, aby spróbować ponownie.")
        } finally {
            if (requestId === loadRequestId.current) setIsLoading(false)
        }
    }, [dispatch])

    useEffect(() => {
        void loadRules()

        return () => {
            // Ignoruj wynik wcześniejszego odczytu po odmontowaniu komponentu.
            ++loadRequestId.current
        }
    }, [loadRules])

    const matrix = useMemo(() => {
        const cellSet = new Set<string>()

        rules
            .filter(rule => rule.documentStatus === selectedStatus)
            .forEach(rule => {
                const subjectType = normalizeSubjectType(rule.subjectType)
                const actionCode = normalizeActionCode(rule.actionCode)

                if (!subjectType) return

                cellSet.add(`${actionCode}__${subjectType}`)
            })

        return cellSet
    }, [rules, selectedStatus])

    const visibleActionCodes = useMemo(() => {
        const query = actionSearch.trim().toLowerCase()
        return query
            ? DOCUMENT_CONTENT_ACTIONS.filter(code => code.toLowerCase().includes(query))
            : DOCUMENT_CONTENT_ACTIONS
    }, [actionSearch])

    const selectedCellKey = selectedActionCode !== null && selectedSubjectType !== null
        ? getCellKey(selectedActionCode, selectedSubjectType)
        : null

    const isSelectedCellEnabled = selectedCellKey !== null
        ? matrix.has(selectedCellKey)
        : false

    const handleStatusChange = (status: DocumentStatus) => {
        if (isContextLocked || saveInProgress.current || status === selectedStatus) return

        setSelectedStatus(status)
        setSelectedActionCode(null)
        setSelectedSubjectType(null)
        setSaveError(null)
    }

    const handleEditModeChange = (nextMode: EditMode) => {
        setEditModeAnchor(null)
        if (isContextLocked || saveInProgress.current || nextMode === editMode) return

        setEditMode(nextMode)
        setSelectedActionCode(nextMode === "row" ? visibleActionCodes[0] ?? null : null)
        setSelectedSubjectType(nextMode === "column"
            ? SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES[0] ?? null
            : null)
        setSaveError(null)
    }

    const handleRefresh = () => {
        // Po błędzie odczytu można ponowić pobranie także z zachowanym szkicem.
        if (isBusy || saveInProgress.current || (hasAnyBatchChanges && hasLoaded)) return
        void loadRules()
    }

    const handleCellClick = (
        actionCode: DocumentContentActions,
        subjectType: SubjectType
    ) => {
        if (!canEdit || saveInProgress.current) return
        if (editMode === "column" && selectedSubjectType !== subjectType) return
        if (editMode === "row" && selectedActionCode !== actionCode) return

        if (editMode === "single") {
            setSelectedActionCode(actionCode)
            setSelectedSubjectType(subjectType)
            return
        }

        const cellKey = getCellKey(actionCode, subjectType)
        const originalEnabled = matrix.has(cellKey)

        setBatchDraft(current => {
            const nextEnabled = !(current[cellKey]?.enabled ?? originalEnabled)
            const next = { ...current }

            if (nextEnabled === originalEnabled) {
                delete next[cellKey]
            } else {
                // Status zapisany w szkicu nie zależy od późniejszego wyboru filtra.
                next[cellKey] = {
                    documentStatus: selectedStatus,
                    actionCode,
                    subjectType,
                    enabled: nextEnabled,
                }
            }

            return next
        })
    }

    const saveChanges = async (changes: DraftRuleChange[], fromBatch: boolean) => {
        if (!canEdit || saveInProgress.current || changes.length === 0) return

        saveInProgress.current = true
        setSaveError(null)
        setPendingCellKeys(new Set(changes.map(change =>
            getCellKey(change.actionCode, change.subjectType))))

        try {
            // Poczekaj na wszystkie wywołania, także gdy któreś zakończy się błędem.
            const results = await Promise.allSettled(changes.map(async change => {
                const result = await dispatch(upsertSysDocumentContentActionRule({
                    dto: {
                        documentStatus: change.documentStatus,
                        subjectType: change.subjectType,
                        actionCode: change.actionCode,
                    },
                    enabled: change.enabled,
                }))

                unwrapResult(result)
            }))

            if (fromBatch) {
                setBatchDraft(current => {
                    const next = { ...current }
                    results.forEach((result, index) => {
                        if (result.status === "fulfilled") {
                            const change = changes[index]
                            delete next[getCellKey(change.actionCode, change.subjectType)]
                        }
                    })
                    return next
                })
            }

            await loadRules()

            const failedCount = results.filter(result => result.status === "rejected").length
            if (failedCount > 0) {
                setSaveError(fromBatch
                    ? `Nie potwierdzono zapisu ${failedCount} z ${changes.length} zmian. Pozostały w szkicu — możesz ponowić zapis.`
                    : "Nie potwierdzono zapisu reguły. Sprawdź jej aktualny stan i spróbuj ponownie.")
            }
        } finally {
            saveInProgress.current = false
            setPendingCellKeys(new Set())
        }
    }

    const handleSave = () => {
        if (editMode !== "single") {
            void saveChanges(Object.values(batchDraft), true)
            return
        }

        if (selectedActionCode === null || selectedSubjectType === null) return

        void saveChanges([{
            documentStatus: selectedStatus,
            actionCode: selectedActionCode,
            subjectType: selectedSubjectType,
            enabled: !isSelectedCellEnabled,
        }], false)
    }

    const handleCancelBatchChanges = () => {
        if (isBusy || saveInProgress.current) return
        setBatchDraft({})
        setSaveError(null)
    }

    const selectionDescription = editMode === "column"
        ? selectedSubjectType !== null
            ? `Kolumna: ${subjectTypeLabel(selectedSubjectType)}`
            : "Nie wybrano kolumny"
        : editMode === "row"
            ? selectedActionCode !== null
                ? `Akcja: ${selectedActionCode}`
                : "Nie wybrano akcji"
            : selectedActionCode !== null && selectedSubjectType !== null
                ? `${selectedActionCode} / ${subjectTypeLabel(selectedSubjectType)}`
                : "Nie wybrano reguły"

    return (
        <Paper
            ref={panelRef}
            variant="outlined"
            sx={{
                display: "flex",
                flexDirection: "column",
                height: panelHeight ?? "100%",
                minHeight: 0,
                minWidth: 0,
                overflow: "hidden",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ px: 1.5, py: 1, flexShrink: 0 }}
            >
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Reguły akcji dokumentu
                    </Typography>
                </Box>
                <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={isBusy || (hasAnyBatchChanges && hasLoaded)}
                    sx={{ flexShrink: 0, textTransform: "none" }}
                >
                    Odśwież
                </Button>
            </Stack>

            <Box sx={{ px: 1.5, pb: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
                    <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: 0 }}>
                        <InputLabel id={statusLabelId}>Reguły dla statusu</InputLabel>
                        <Select
                            labelId={statusLabelId}
                            value={selectedStatus}
                            label="Reguły dla statusu"
                            disabled={isContextLocked}
                            onChange={event => handleStatusChange(event.target.value as DocumentStatus)}
                        >
                            {DOCUMENT_STATUSES.map(status => (
                                <MenuItem key={status} value={status}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <DocumentStatusLabel status={status} />
                                        <Typography variant="body2" color="text.secondary">
                                            ({status})
                                        </Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        type="search"
                        label="Szukaj akcji"
                        value={actionSearch}
                        onChange={event => setActionSearch(event.target.value)}
                        sx={{ flex: "1 1 200px", minWidth: 0 }}
                    />

                    <Button
                        id={modeButtonId}
                        variant="outlined"
                        endIcon={<ExpandMoreIcon />}
                        aria-controls={editModeAnchor ? modeMenuId : undefined}
                        aria-haspopup="menu"
                        aria-expanded={Boolean(editModeAnchor)}
                        disabled={isContextLocked}
                        onClick={event => setEditModeAnchor(event.currentTarget)}

                        sx={theme => ({
                            minHeight: 40,
                            maxWidth: "100%",
                            ml: "auto",
                            textTransform: "none",
                            bgcolor: editMode !== "single"
                                ? alpha(theme.palette.primary.main, 0.08)
                                : undefined,
                        })}
                    >
                        Edycja: {editModeLabel}
                    </Button>

                    <Menu
                        id={modeMenuId}
                        anchorEl={editModeAnchor}
                        open={Boolean(editModeAnchor)}
                        onClose={() => setEditModeAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        sx={{ "& .MuiPaper-root": { mt: 0.75, minWidth: 270, maxWidth: "calc(100vw - 32px)" } }}
                    >
                        {EDIT_MODES.map(mode => (
                            <MenuItem
                                key={mode.value}
                                selected={editMode === mode.value}
                                role="menuitemradio"
                                aria-checked={editMode === mode.value}
                                onClick={() => handleEditModeChange(mode.value)}
                                sx={{ gap: 1.5, py: 1, whiteSpace: "normal" }}
                            >
                                <Box sx={{ width: 20, flexShrink: 0, display: "flex", color: "primary.main" }}>
                                    {editMode === mode.value && <CheckIcon fontSize="small" />}
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {mode.label}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>

                {loadError && <Alert severity="error" sx={{ mt: 1 }}>{loadError}</Alert>}
                {saveError && <Alert severity="error" sx={{ mt: 1 }}>{saveError}</Alert>}
            </Box>

            <Box sx={{ height: 3, flexShrink: 0 }}>{isBusy && <LinearProgress sx={{ height: 3 }} />}</Box>

            <TableContainer sx={{ flex: "1 1 0%", minHeight: 0, minWidth: 0, overflow: "auto" }}>
                <Table stickyHeader size="small" aria-label="Reguły akcji dokumentu">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 220, fontWeight: 700, position: "sticky", left: 0, zIndex: 4, bgcolor: "grey.100" }}>
                                Akcja
                            </TableCell>
                            {SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES.map(subjectType => {
                                const selected = selectedSubjectType === subjectType
                                    && (editMode === "column" || (editMode === "single" && selectedCellKey !== null))
                                return (
                                    <TableCell key={subjectType} align="center" sx={{ minWidth: 150, p: 0, bgcolor: "grey.100" }}>
                                        <ButtonBase
                                            disabled={editMode !== "column" || !canEdit || hasAnyBatchChanges}
                                            onClick={() => setSelectedSubjectType(subjectType)}
                                            aria-label={`Wybierz kolumnę: ${subjectTypeLabel(subjectType)}`}
                                            aria-pressed={selected}
                                            sx={theme => ({
                                                width: "100%", minHeight: 48, px: 1.5, py: 1, fontWeight: 600,
                                                color: selected ? "success.main" : "text.primary",
                                                bgcolor: selected
                                                    ? alpha(theme.palette.success.main, SELECTION_OPACITY.axis)
                                                    : theme.palette.grey[100],
                                                boxShadow: selected
                                                    ? `inset 2px 0 0 ${theme.palette.success.main}, inset -2px 0 0 ${theme.palette.success.main}, inset 0 2px 0 ${theme.palette.success.main}`
                                                    : undefined,
                                                "&:hover": {
                                                    bgcolor: selected
                                                        ? alpha(theme.palette.success.main, SELECTION_OPACITY.axis)
                                                        : theme.palette.grey[200],
                                                },
                                                "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "success.main", outlineOffset: -2 },
                                            })}
                                        >
                                            {subjectTypeLabel(subjectType)}
                                        </ButtonBase>
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {!hasLoaded ? (
                            <TableRow>
                                <TableCell colSpan={SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                    {isLoading ? "Ładowanie reguł…" : "Reguły są niedostępne. Ponów pobranie danych."}
                                </TableCell>
                            </TableRow>
                        ) : visibleActionCodes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                    Brak akcji pasujących do wyszukiwania.
                                </TableCell>
                            </TableRow>
                        ) : visibleActionCodes.map((actionCode, actionIndex) => {
                            const rowSelected = selectedActionCode === actionCode
                                && (editMode === "row" || (editMode === "single" && selectedCellKey !== null))
                            const isLastRow = actionIndex === visibleActionCodes.length - 1
                            return (
                                <TableRow key={actionCode}>
                                    <TableCell component="th" scope="row" sx={{ p: 0, position: "sticky", left: 0, zIndex: 1, bgcolor: "grey.50" }}>
                                        <ButtonBase
                                            disabled={editMode !== "row" || !canEdit || hasAnyBatchChanges}
                                            onClick={() => setSelectedActionCode(actionCode)}
                                            aria-label={`Wybierz akcję: ${actionCode}`}
                                            aria-pressed={rowSelected}
                                            sx={theme => ({
                                                width: "100%", minHeight: 44, px: 2, py: 1,
                                                justifyContent: "flex-start", textAlign: "left", fontWeight: 600,
                                                color: rowSelected ? "success.main" : "text.primary",
                                                bgcolor: rowSelected
                                                    ? alpha(theme.palette.success.main, SELECTION_OPACITY.axis)
                                                    : theme.palette.grey[50],
                                                boxShadow: rowSelected
                                                    ? `inset 2px 0 0 ${theme.palette.success.main}, inset 0 2px 0 ${theme.palette.success.main}, inset 0 -2px 0 ${theme.palette.success.main}`
                                                    : undefined,
                                                "&:hover": {
                                                    bgcolor: rowSelected
                                                        ? alpha(theme.palette.success.main, SELECTION_OPACITY.axis)
                                                        : theme.palette.grey[100],
                                                },
                                                "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "success.main", outlineOffset: -2 },
                                            })}
                                        >
                                            {actionCode}
                                        </ButtonBase>
                                    </TableCell>

                                    {SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES.map((subjectType, subjectIndex) => {
                                        const cellKey = getCellKey(actionCode, subjectType)
                                        const draft = batchDraft[cellKey]
                                        const isEnabled = draft?.enabled ?? matrix.has(cellKey)
                                        const isPending = pendingCellKeys.has(cellKey)
                                        const columnSelected = selectedSubjectType === subjectType
                                            && (editMode === "column" || (editMode === "single" && selectedCellKey !== null))
                                        const cellSelected = editMode === "single" && selectedCellKey === cellKey
                                        const isLastColumn = subjectIndex === SYS_DOCUMENT_CONTENT_ACTION_RULE_SUBJECT_TYPES.length - 1
                                        const outsideScope = (editMode === "column" && !columnSelected)
                                            || (editMode === "row" && !rowSelected)

                                        return (
                                            <TableCell key={cellKey} align="center" sx={{ p: 0 }}>
                                                <ButtonBase
                                                    disabled={!canEdit || outsideScope}
                                                    onClick={() => handleCellClick(actionCode, subjectType)}
                                                    aria-label={`${actionCode}, ${subjectTypeLabel(subjectType)}: ${isEnabled ? "włączona" : "wyłączona"}${draft ? ", niezapisana zmiana" : ""}`}
                                                    aria-pressed={editMode === "single" ? cellSelected : undefined}
                                                    aria-busy={isPending}
                                                    sx={theme => ({
                                                        position: "relative", width: "100%", minHeight: 44, py: 1,
                                                        color: isEnabled ? RULE_COLORS.enabled : RULE_COLORS.disabled,
                                                        bgcolor: cellSelected || rowSelected || columnSelected
                                                            ? alpha(theme.palette.success.main, cellSelected ? SELECTION_OPACITY.cell : SELECTION_OPACITY.axis)
                                                            : undefined,
                                                        boxShadow: cellSelected
                                                            ? `inset 0 0 0 3px ${theme.palette.success.main}`
                                                            : [
                                                                rowSelected ? `inset 0 2px 0 ${theme.palette.success.main}` : null,
                                                                rowSelected || (columnSelected && isLastRow)
                                                                    ? `inset 0 -2px 0 ${theme.palette.success.main}` : null,
                                                                columnSelected ? `inset 2px 0 0 ${theme.palette.success.main}` : null,
                                                                columnSelected || (rowSelected && isLastColumn)
                                                                    ? `inset -2px 0 0 ${theme.palette.success.main}` : null,
                                                            ].filter(Boolean).join(", ") || undefined,
                                                        "&:hover": {
                                                            bgcolor: alpha(theme.palette.success.main,
                                                                cellSelected ? 0.24 : rowSelected || columnSelected ? 0.14 : 0.04),
                                                        },
                                                        "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "success.main", outlineOffset: -2 },
                                                    })}
                                                >
                                                    {isPending ? <CircularProgress size={18} /> : isEnabled
                                                        ? <CheckIcon fontSize="small" />
                                                        : <Typography component="span" variant="body2">—</Typography>}
                                                    {draft && (
                                                        <Box title="Niezapisana zmiana" aria-hidden="true" sx={{ position: "absolute", top: 7, right: 9, width: 7, height: 7, borderRadius: "50%", bgcolor: RULE_COLORS.changed }} />
                                                    )}
                                                </ButtonBase>
                                            </TableCell>
                                        )
                                    })}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{
                flexShrink: 0,
                px: 1.5, py: 1, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap",
            }}>
                <Box aria-live="polite" sx={{ flex: "1 1 180px", minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={selectionDescription} sx={{ fontWeight: 600 }}>
                        {selectionDescription}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ ml: "auto", flexShrink: 0 }}>
                    {editMode !== "single" && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleCancelBatchChanges}
                            disabled={isBusy || !hasAnyBatchChanges}
                            sx={{ textTransform: "none" }}
                        >
                            Odrzuć zmiany
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={handleSave}
                        disabled={!canEdit || (editMode === "single" ? selectedCellKey === null : !hasAnyBatchChanges)}
                        startIcon={isSaving
                            ? <CircularProgress size={16} color="inherit" />
                            : editMode === "single"
                                ? isSelectedCellEnabled ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />
                                : undefined}
                        color={
                            editMode !== "single"
                                ? "primary"
                                : isSelectedCellEnabled
                                    ? "error"
                                    : "success"
                        }
                        sx={{ textTransform: "none" }}

                    >
                        {isSaving ? "Zapisywanie…" : editMode !== "single"
                            ? `Zapisz zmiany (${batchChangesCount})`
                            : isSelectedCellEnabled ? "Wyłącz regułę" : "Włącz regułę"}
                    </Button>
                </Stack>
            </Box>
        </Paper>
    )
}

export default SysDocumentContentActionRulesSetup
